import Highcharts from '~/scripts/integrations/highcharts';

class EmployeeCostCalculator {
  constructor() {
    this.form = document.getElementById('calculator-form');
    this.baseSalaryInput = document.getElementById('base-salary');
    this.healthInsuranceInput = document.getElementById('health-insurance');
    this.dentalInsuranceInput = document.getElementById('dental-insurance');
    this.visionInsuranceInput = document.getElementById('vision-insurance');
    this.lifeInsuranceInput = document.getElementById('life-insurance');
    this.disabilityInsuranceInput = document.getElementById('disability-insurance');
    this.retirementMatchInput = document.getElementById('retirement-match');
    this.otherBenefitsInput = document.getElementById('other-benefits');
    this.socialSecurityInput = document.getElementById('social-security');
    this.medicareInput = document.getElementById('medicare');
    this.stateUnemploymentInput = document.getElementById('state-unemployment');
    this.federalUnemploymentInput = document.getElementById('federal-unemployment');
    this.overheadInput = document.getElementById('overhead');
    
    // Hiring costs inputs
    this.recruitmentFeesInput = document.getElementById('recruitment-fees');
    this.jobBoardCostsInput = document.getElementById('job-board-costs');
    this.backgroundChecksInput = document.getElementById('background-checks');
    this.onboardingCostsInput = document.getElementById('onboarding-costs');
    this.otherHiringCostsInput = document.getElementById('other-hiring-costs');
    
    // Future planning inputs
    this.annualRaisePercentInput = document.getElementById('annual-raise-percent');
    this.planningPeriodInput = document.getElementById('planning-period');
    
    this.totalCost = document.getElementById('total-cost');
    this.costText = document.getElementById('cost-text');
    this.futureProjections = document.getElementById('future-projections');
    this.projectionsTable = document.getElementById('projections-table');

    // Chart instance
    this.employeeCostChart = null;

    this.resetBtn = document.getElementById('reset-employee-cost-calculator');

    this.bindEvents();
    this.calculateCost();
  }

  bindEvents() {
    [
      this.baseSalaryInput,
      this.healthInsuranceInput,
      this.dentalInsuranceInput,
      this.visionInsuranceInput,
      this.lifeInsuranceInput,
      this.disabilityInsuranceInput,
      this.retirementMatchInput,
      this.otherBenefitsInput,
      this.socialSecurityInput,
      this.medicareInput,
      this.stateUnemploymentInput,
      this.federalUnemploymentInput,
      this.overheadInput,
      this.recruitmentFeesInput,
      this.jobBoardCostsInput,
      this.backgroundChecksInput,
      this.onboardingCostsInput,
      this.otherHiringCostsInput,
      this.annualRaisePercentInput,
      this.planningPeriodInput
    ].forEach(input => {
      input.addEventListener('input', () => this.calculateCost());
      input.addEventListener('change', () => this.calculateCost());
    });

    if (this.resetBtn) {
      this.resetBtn.addEventListener('click', () => this.reset());
    }
  }

  formatCurrency(value) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  }

  updateEmployeeCostChart(baseSalary, benefits, payrollTaxes, overhead, hiringCosts, totalCost) {
    if (this.employeeCostChart) {
      this.employeeCostChart.destroy();
    }

    const chartData = [
      {
        name: 'Base Salary',
        y: baseSalary,
        color: '#28a745'
      },
      {
        name: 'Benefits',
        y: benefits,
        color: '#17a2b8'
      },
      {
        name: 'Payroll Taxes',
        y: payrollTaxes,
        color: '#ffc107'
      },
      {
        name: 'Overhead',
        y: overhead,
        color: '#6f42c1'
      }
    ];

    // Only add hiring costs if they exist
    if (hiringCosts > 0) {
      chartData.push({
        name: 'Hiring Costs',
        y: hiringCosts,
        color: '#e83e8c'
      });
    }

    this.employeeCostChart = Highcharts.chart('employee-cost-chart', {
      chart: {
        type: 'pie',
        height: 400
      },
      title: {
        text: 'Employee Cost Breakdown'
      },
      tooltip: {
        pointFormat: '<b>{point.name}</b><br/>' +
                    'Amount: {point.y:,.0f}<br/>' +
                    'Percentage: {point.percentage:.1f}%'
      },
      plotOptions: {
        pie: {
          dataLabels: {
            enabled: true,
            format: '<b>{point.name}</b><br/>${point.y:,.0f}'
          }
        }
      },
      series: [{
        name: 'Cost Components',
        data: chartData
      }]
    });
  }

  calculateCost() {
    const baseSalary = parseFloat(this.baseSalaryInput.value) || 0;
    const healthInsurance = parseFloat(this.healthInsuranceInput.value) || 0;
    const dentalInsurance = parseFloat(this.dentalInsuranceInput.value) || 0;
    const visionInsurance = parseFloat(this.visionInsuranceInput.value) || 0;
    const lifeInsurance = parseFloat(this.lifeInsuranceInput.value) || 0;
    const disabilityInsurance = parseFloat(this.disabilityInsuranceInput.value) || 0;
    const retirementMatch = parseFloat(this.retirementMatchInput.value) || 0;
    const otherBenefits = parseFloat(this.otherBenefitsInput.value) || 0;
    const socialSecurity = parseFloat(this.socialSecurityInput.value) || 0;
    const medicare = parseFloat(this.medicareInput.value) || 0;
    const stateUnemployment = parseFloat(this.stateUnemploymentInput.value) || 0;
    const federalUnemployment = parseFloat(this.federalUnemploymentInput.value) || 0;
    const overhead = parseFloat(this.overheadInput.value) || 0;

    // Hiring costs
    const recruitmentFees = parseFloat(this.recruitmentFeesInput.value) || 0;
    const jobBoardCosts = parseFloat(this.jobBoardCostsInput.value) || 0;
    const backgroundChecks = parseFloat(this.backgroundChecksInput.value) || 0;
    const onboardingCosts = parseFloat(this.onboardingCostsInput.value) || 0;
    const otherHiringCosts = parseFloat(this.otherHiringCostsInput.value) || 0;
    const hiringCosts = recruitmentFees + jobBoardCosts + backgroundChecks + onboardingCosts + otherHiringCosts;

    // Future planning
    const annualRaisePercent = parseFloat(this.annualRaisePercentInput.value) || 0;
    const planningPeriod = parseInt(this.planningPeriodInput.value) || 2;

    // Calculate totals
    const benefits = healthInsurance + dentalInsurance + visionInsurance + lifeInsurance + 
                    disabilityInsurance + retirementMatch + otherBenefits;
    const payrollTaxes = socialSecurity + medicare + stateUnemployment + federalUnemployment;
    const totalCost = baseSalary + benefits + payrollTaxes + overhead + hiringCosts;

    // Calculate percentages
    const salaryPercentage = totalCost > 0 ? (baseSalary / totalCost) * 100 : 0;
    const benefitsPercentage = totalCost > 0 ? (benefits / totalCost) * 100 : 0;
    const taxesPercentage = totalCost > 0 ? (payrollTaxes / totalCost) * 100 : 0;
    const overheadPercentage = totalCost > 0 ? (overhead / totalCost) * 100 : 0;
    const hiringPercentage = totalCost > 0 ? (hiringCosts / totalCost) * 100 : 0;

    // Update display
    this.totalCost.textContent = this.formatCurrency(totalCost);
    this.costText.innerHTML = `
      Total annual employee cost is ${this.formatCurrency(totalCost)}.
      <br><small class="text-muted">
        Base salary: ${salaryPercentage.toFixed(1)}% | 
        Benefits: ${benefitsPercentage.toFixed(1)}% | 
        Taxes: ${taxesPercentage.toFixed(1)}% | 
        Overhead: ${overheadPercentage.toFixed(1)}%${hiringCosts > 0 ? ` | Hiring: ${hiringPercentage.toFixed(1)}%` : ''}
      </small>
    `;

    // Update chart
    this.updateEmployeeCostChart(baseSalary, benefits, payrollTaxes, overhead, hiringCosts, totalCost);

    // Update future projections
    this.updateFutureProjections(baseSalary, benefits, payrollTaxes, overhead, hiringCosts, annualRaisePercent, planningPeriod);
  }

  updateFutureProjections(baseSalary, benefits, payrollTaxes, overhead, hiringCosts, annualRaisePercent, planningPeriod) {
    if (planningPeriod <= 1 || annualRaisePercent <= 0) {
      this.futureProjections.style.display = 'none';
      return;
    }

    this.futureProjections.style.display = 'block';
    let currentSalary = baseSalary;
    let currentTotalCost = baseSalary + benefits + payrollTaxes + overhead + hiringCosts;
    let previousTotalCost = currentTotalCost;

    let tableHTML = '';
    
    for (let year = 1; year <= planningPeriod; year++) {
      if (year === 1) {
        // Current year (no raise yet)
        tableHTML += `
          <tr>
            <td>Year ${year} (Current)</td>
            <td>${this.formatCurrency(currentSalary)}</td>
            <td>${this.formatCurrency(currentTotalCost)}</td>
            <td>-</td>
          </tr>
        `;
      } else {
        // Future years with raises
        const salaryIncrease = currentSalary * (annualRaisePercent / 100);
        currentSalary += salaryIncrease;
        
        // Recalculate taxes based on new salary (assuming same percentages)
        const newSocialSecurity = currentSalary * 0.062;
        const newMedicare = currentSalary * 0.0145;
        const newPayrollTaxes = newSocialSecurity + newMedicare + 
                               parseFloat(this.stateUnemploymentInput.value || 0) + 
                               parseFloat(this.federalUnemploymentInput.value || 0);
        
        // Recalculate benefits (assuming they stay the same for simplicity)
        const newTotalCost = currentSalary + benefits + newPayrollTaxes + overhead + hiringCosts;
        const costIncrease = newTotalCost - previousTotalCost;
        const increasePercentage = previousTotalCost > 0 ? (costIncrease / previousTotalCost) * 100 : 0;
        
        tableHTML += `
          <tr>
            <td>Year ${year}</td>
            <td>${this.formatCurrency(currentSalary)}</td>
            <td>${this.formatCurrency(newTotalCost)}</td>
            <td class="${costIncrease > 0 ? 'text-success' : 'text-danger'}">
              ${costIncrease > 0 ? '+' : ''}${this.formatCurrency(costIncrease)} (${increasePercentage.toFixed(1)}%)
            </td>
          </tr>
        `;
        
        previousTotalCost = newTotalCost;
      }
    }
    
    this.projectionsTable.innerHTML = tableHTML;
  }


  reset() {
    this.form.reset();
    this.calculateCost();
  }
}

export default EmployeeCostCalculator;
